/**
 * Client Isolation Context Provider for SCULPTD APP
 * Provides client context throughout the app without changing UI aesthetics
 */

import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { clientIsolationService, ClientContext } from '../services/ClientIsolationService';

interface ClientIsolationContextType {
  clientContext: ClientContext | null;
  isLoading: boolean;
  error: string | null;
  getClientHeaders: () => Record<string, string>;
  createIsolatedRequest: (url: string, options?: RequestInit) => RequestInit;
  validateAccess: (operation: string) => boolean;
  encryptData: (data: any) => string;
  decryptData: (encryptedData: string) => any;
  refreshContext: () => void;
}

const ClientIsolationContext = createContext<ClientIsolationContextType | undefined>(undefined);

interface ClientIsolationProviderProps {
  children: ReactNode;
}

export const ClientIsolationProvider: React.FC<ClientIsolationProviderProps> = ({ children }) => {
  const [clientContext, setClientContext] = useState<ClientContext | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const initializeClient = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Get existing client context
        const context = clientIsolationService.getClientContext();
        
        if (context) {
          setClientContext(context);
        } else {
          // Wait for initialization from App.tsx
          const checkInterval = setInterval(() => {
            const context = clientIsolationService.getClientContext();
            if (context) {
              setClientContext(context);
              clearInterval(checkInterval);
              setIsLoading(false);
            }
          }, 100);

          // Timeout after 5 seconds
          setTimeout(() => {
            clearInterval(checkInterval);
            if (!clientIsolationService.getClientContext()) {
              setError('Client context initialization timeout');
            }
            setIsLoading(false);
          }, 5000);
        }
        
        setIsLoading(false);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to initialize client context');
        setIsLoading(false);
      }
    };

    initializeClient();
  }, []);

  const getClientHeaders = (): Record<string, string> => {
    if (!clientContext) {
      throw new Error('Client context not initialized');
    }
    return clientIsolationService.getClientHeaders();
  };

  const createIsolatedRequest = (url: string, options: RequestInit = {}): RequestInit => {
    if (!clientContext) {
      throw new Error('Client context not initialized');
    }
    return clientIsolationService.createIsolatedRequest(url, options);
  };

  const validateAccess = (operation: string): boolean => {
    if (!clientContext) {
      return false;
    }
    return clientIsolationService.validateAccess(operation);
  };

  const encryptData = (data: any): string => {
    if (!clientContext) {
      throw new Error('Client context not initialized');
    }
    return clientIsolationService.encryptData(data);
  };

  const decryptData = (encryptedData: string): any => {
    if (!clientContext) {
      throw new Error('Client context not initialized');
    }
    return clientIsolationService.decryptData(encryptedData);
  };

  const refreshContext = () => {
    const context = clientIsolationService.getClientContext();
    setClientContext(context);
  };

  const value: ClientIsolationContextType = {
    clientContext,
    isLoading,
    error,
    getClientHeaders,
    createIsolatedRequest,
    validateAccess,
    encryptData,
    decryptData,
    refreshContext
  };

  return (
    <ClientIsolationContext.Provider value={value}>
      {children}
    </ClientIsolationContext.Provider>
  );
};

export const useClientIsolationContext = (): ClientIsolationContextType => {
  const context = useContext(ClientIsolationContext);
  if (context === undefined) {
    throw new Error('useClientIsolationContext must be used within a ClientIsolationProvider');
  }
  return context;
};